import React, { useState, useEffect } from "react";
import GridTable from "../../components/GridTable";
import { GridColDef } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { GridCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LockResetIcon from '@mui/icons-material/LockReset';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from 'bcryptjs';
import TextField from '../../components/TextField';
import ActionMenu from '../../components/ActionMenu';
import Dialog from "../../components/Dialog";
import Dropdown from '../../components/Dropdown'
import Loader from '../../components/Loader'
import Button from '../../components/Button'
import { successMessage, errorMessage } from '../../components/Toaster';
import { getAllUsers, createUser, updateUser, deleteUser, getUserRole, createUserRole, updateUserRole } from './api'
import { getAllCompanies } from '../company/api'
import { getAllRoles } from '../role/api'
import { RoleData } from '../role/interface'
import { CompanyData } from '../company/interface'
import { ApiResponse, UserData, CreateUserParams, UserRoleParams } from './interface'
import { resetPassword } from '../login/api'
import { getErrorMessage } from '../../utils/error'

const User: React.FC = () => {
    const loggedInUserId = localStorage.getItem("user_id");
    const [users, setUsers] = useState<UserData[]>([]);
    const [companies, setCompanies] = useState<CompanyData[]>([]);
    const [roles, setRoles] = useState<RoleData[]>([]);
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState<UserData | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [userToAttach, setUserToAttach] = useState<GridCellParams | null>(null);
    const [isUserRoleAttachDialogOpen, setUserRoleAttachDialogOpen] = useState(false);
    const [loading, setLoading] = useState({
        update: false,
        delete: false,
        create: false,
        fetch: false,
    });

    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required("First Name is required"),
        lastName: Yup.string().required("Last Name is required"),
        userName: Yup.string().required("User Name is required"),
        password: Yup.string().when("editUser", {
            is: () => !editUser,
            then: (schema) => schema.required("Password is required"),
            otherwise: (schema) => schema,
        }),
        companyId: Yup.string().required("Company is required"),
        languagePreference: Yup.string().required("Language Preference is required"),
        phoneNumber: Yup.string()
            .when('email', {
                is: (email: string | null) => Boolean(email),
                then: (schema) => schema.nullable(),
                otherwise: (schema) =>
                    schema
                        .required('Either Email or Phone Number is required')
                        .matches(/^\d{10}$/, 'Phone Number must be exactly 10 digits')
            }),
        email: Yup.string()
            .email('Invalid email format')
            .nullable(),
    });

    const validationSchemaForRole = Yup.object().shape({
        role: Yup.array()
            .of(Yup.string().required("Role is required"))
            .min(1, "At least one role is required")
    });

    const columns: GridColDef[] = [
        { field: "first_name", headerName: "First Name", flex: 1 },
        { field: "last_name", headerName: "Last Name", flex: 1 },
        { field: "user_name", headerName: "User Name", flex: 1 },
        {
            field: "contact_info",
            headerName: "Phone/Email",
            flex: 1,
            renderCell: (params: GridCellParams) => {
                const phoneNumber = params.row?.phone_number;
                const email = params.row?.email;
                return <div>{phoneNumber || email || "N/A"}</div>;
            },
        },
        { field: "company_name", headerName: "Company Name", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => {
                const isDisabled = loggedInUserId === params.row.user_id;
                return (
                    <ActionMenu
                        params={params}
                        actions={[
                            {
                                icon: <EditIcon color="primary" />,
                                tooltip: "Edit",
                                onClick: handleEdit,
                            },
                            {
                                icon: <DeleteIcon color="error" />,
                                tooltip: "Delete",
                                onClick: handleDelete,
                                disabled: isDisabled,
                            },
                            {
                                icon: <BadgeOutlinedIcon color="success" />,
                                tooltip: "Role",
                                onClick: handleAttachRole,
                            },
                            {
                                icon: <LockResetIcon color="primary" />,
                                tooltip: "Reset Password",
                                onClick: handleResetPassword,
                                disabled: isDisabled,
                            },
                        ]}
                    />
                );
            },
            sortable: false,
            filterable: false,
            flex: 1,
        }

    ];

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const usersData: ApiResponse<UserData[]> = await getAllUsers();
                if (usersData.status && usersData.data) {
                    setUsers(usersData.data);
                } else {
                    errorMessage(usersData.message || "Failed to fetch users.");
                }
            } catch (error) {
                console.error("Error fetching users:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "Error fetching users");
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchUsers();
    }, []);


    const openDialog = async () => {
        setDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const companyData: ApiResponse<CompanyData[]> = await getAllCompanies();
            if (companyData.status && companyData.data) {
                setCompanies(companyData.data);
            } else {
                errorMessage(companyData.message || "Failed to fetch companies.");
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "Error fetching companies");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setDialogOpen(false);
        setEditUser(null);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditUser(params.row as UserData);
        openDialog();
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        if (editUser) {
            await handleUpdate(values);
        } else {
            await handleCreate(values);
        }
        closeDialog(resetForm);
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const hashedPassword = await bcrypt.hash(values.password, 10);
            const newUser: CreateUserParams = {
                first_name: values.firstName.trim(),
                last_name: values.lastName.trim(),
                user_name: values.userName.trim(),
                password: hashedPassword,
                company_id: values.companyId,
                language_preference: values.languagePreference,
                phone_number: values?.phoneNumber?.trim(),
                email: values?.email?.trim() || null,
            };

            const response = await createUser(newUser);

            if (response?.status) {
                const company = companies.find((c) => c.company_id === values.companyId);
                setUsers((prevUsers) => {
                    const currentUsers = Array.isArray(prevUsers) ? prevUsers : [];
                    return [
                        ...currentUsers,
                        {
                            user_id: response?.data?.user_id,
                            first_name: values.firstName,
                            last_name: values.lastName,
                            user_name: values.userName,
                            phone_number: values.phoneNumber,
                            email: values.email,
                            company_id: values.companyId,
                            company_name: company?.company_name,
                            language_preference: values.languagePreference
                        }
                    ];
                });
                successMessage(response.message || "User created successfully!");
            } else {
                errorMessage(response?.message || "Failed to create user. Please try again.");
            }
        } catch (error) {
            console.error("Error creating user:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedUserData = {
                user_id: editUser!.user_id,
                first_name: values.firstName.trim(),
                last_name: values.lastName.trim(),
                user_name: values.userName.trim(),
                phone_number: values?.phoneNumber?.trim(),
                email: values?.email?.trim() || null,
                company_id: values.companyId,
            };

            const response = await updateUser(updatedUserData);

            if (response.status) {
                const updatedUser = updatedUserData;
                const company = companies.find((c) => c.company_id === updatedUser.company_id);
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        user.user_id === updatedUser.user_id
                            ? { ...user, ...updatedUser, company_name: company?.company_name || "N/A" }
                            : user
                    )
                );
                successMessage(response.message || "User updated successfully!");
            } else {
                errorMessage(response.message || "Failed to update user. Please try again.");
            }
        } catch (error) {
            console.error("Error updating user:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "An unexpected error occurred. Please try again.");
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));

        if (itemToDelete) {
            try {
                const response = await deleteUser(itemToDelete.row.user_id);

                if (response.status) {
                    setUsers((prevUsers) =>
                        prevUsers.filter((user) => user.user_id !== itemToDelete.row.user_id)
                    );
                    successMessage(response.message || "User deleted successfully!");
                } else {
                    errorMessage(response.message || "Failed to delete user. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting user:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "An unexpected error occurred. Please try again.");
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    const handleAttachRole = async (params: GridCellParams) => {
        setLoading((prev) => ({ ...prev, fetch: true }));
        setUserToAttach(params);
        try {
            const rolesData: ApiResponse<RoleData[]> = await getAllRoles();
            if (rolesData.status && rolesData.data) {
                setRoles(rolesData.data);
            } else {
                errorMessage(rolesData.message || "Failed to fetch roles.");
            }
            const userRoleResponse: ApiResponse<string[]> = await getUserRole({
                user_id: params.row.user_id,
                company_id: params.row.company_id,
            });
            if (userRoleResponse.status && Array.isArray(userRoleResponse.data)) {
                setUserRoles(userRoleResponse.data.flat());
            } else {
                errorMessage(rolesData.message || "Failed to fetch user roles.");
            }
            setUserRoleAttachDialogOpen(true);
        } catch (error) {
            console.error("Failed to fetch roles or user roles:", error);
            const message = getErrorMessage(error);
            errorMessage(message)
        }
        setLoading((prev) => ({ ...prev, fetch: false }));
    };

    const confirmDialogForRole = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        if (userRoles?.length > 0) {
            await UpdateUserRole(values);
        } else {
            await CreateUserRole(values);
        }
        closeUserRoleAttachModel(resetForm);
    };

    const closeUserRoleAttachModel = (resetForm: () => void) => {
        setUserRoleAttachDialogOpen(false);
        setUserToAttach(null)
        resetForm();
        setUserRoles([])
    };

    const CreateUserRole = async (values: any) => {
        if (userToAttach) {
            setLoading((prev) => ({ ...prev, create: true }));
            try {
                const userData: UserRoleParams = {
                    company_id: userToAttach.row.company_id,
                    user_id: userToAttach.row.user_id,
                    role_names: values.role,
                };
                const result = await createUserRole(userData);
                if (result.status) {
                    successMessage(result.message)
                }
            } catch (error) {
                console.error("Error attaching role:", error);
                const message = getErrorMessage(error);
                errorMessage(message)
            }
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const UpdateUserRole = async (values: any) => {
        if (userToAttach) {
            setLoading((prev) => ({ ...prev, update: true }));
            try {
                const userData: UserRoleParams = {
                    company_id: userToAttach.row.company_id,
                    user_id: userToAttach.row.user_id,
                    role_names: values.role,
                };
                const result = await updateUserRole(userData);
                if (result.status) {
                    successMessage(result.message)
                }
            } catch (error) {
                console.error("Error updating user role:", error);
                const message = getErrorMessage(error);
                errorMessage(message)
            }
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleResetPassword = async (params: GridCellParams) => {
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const hashedPassword = await bcrypt.hash("test@123", 10);
            const response = await resetPassword({ user_id: params?.row?.user_id, password: hashedPassword });
            successMessage(response.message);
        } catch (error) {
            const message = getErrorMessage(error);
            errorMessage(message || 'Reset Password failed.');
        }
        setLoading((prev) => ({ ...prev, fetch: false }));
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <div>
                            <div className='d-flex justify-end'>
                                <div className='d-flex justify-between w-100'>
                                    <TextField
                                        className='w-25'
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '100%', width: '100%' }} className="ag-theme-alpine m-t-20">
                            <GridTable
                                rows={users}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.user_name}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                firstName: editUser ? editUser.first_name : "",
                                lastName: editUser ? editUser.last_name : "",
                                userName: editUser ? editUser.user_name : "",
                                phoneNumber: editUser ? editUser.phone_number : null,
                                email: editUser ? editUser.email : null,
                                password: editUser ? "" : "",
                                companyId: editUser ? editUser.company_id : "",
                                languagePreference: editUser ? editUser.language_preference : "EN",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue,
                            }) => (
                                <>
                                    <Dialog
                                        open={isDialogOpen}
                                        onClose={() => closeDialog(resetForm)}
                                        onConfirm={handleSubmit}
                                        title={
                                            !loading.update && !loading.create
                                                ? editUser
                                                    ? "Edit User"
                                                    : "Add New User"
                                                : ""
                                        }
                                        confirmButtonText={
                                            !loading.update && !loading.create
                                                ? editUser
                                                    ? "Update"
                                                    : "Add"
                                                : ""
                                        }
                                        cancelButtonText={
                                            !loading.update && !loading.create ? "Cancel" : ""
                                        }
                                        icon={<PersonAddIcon />}
                                        message={
                                            <Box padding={4}>
                                                {loading.update || loading.create ? (
                                                    <Loader
                                                        color="primary"
                                                        size={40}
                                                    />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="firstName"
                                                                    name="firstName"
                                                                    label="First Name"
                                                                    required
                                                                    value={values.firstName}
                                                                    onChange={handleChange}
                                                                    error={touched.firstName && Boolean(errors.firstName)}
                                                                    helperText={touched.firstName && errors.firstName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="lastName"
                                                                    name="lastName"
                                                                    label="Last Name"
                                                                    required
                                                                    value={values.lastName}
                                                                    onChange={handleChange}
                                                                    error={touched.lastName && Boolean(errors.lastName)}
                                                                    helperText={touched.lastName && errors.lastName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="userName"
                                                                    name="userName"
                                                                    label="User Name"
                                                                    required
                                                                    value={values.userName}
                                                                    onChange={handleChange}
                                                                    error={touched.userName && Boolean(errors.userName)}
                                                                    helperText={touched.userName && errors.userName}
                                                                />
                                                            </Grid>
                                                            {!editUser &&
                                                                <Grid size={4}>
                                                                    <TextField
                                                                        id="password"
                                                                        name="password"
                                                                        label="Password"
                                                                        type="password"
                                                                        value={values.password}
                                                                        onChange={handleChange}
                                                                        error={touched.password && Boolean(errors.password)}
                                                                        helperText={touched.password && errors.password}
                                                                    />
                                                                </Grid>}
                                                            <Grid size={4}>
                                                                {companies?.length > 0 && (
                                                                    <Dropdown
                                                                        options={companies.map((pref) => ({
                                                                            id: pref.company_id,
                                                                            name: pref.company_name,
                                                                        }))}
                                                                        value={values.companyId}
                                                                        onChange={(id) => setFieldValue("companyId", id)}
                                                                        label="Company"
                                                                        required
                                                                        error={Boolean(touched.companyId && errors.companyId)}
                                                                        helperText={touched.companyId && errors.companyId ? String(errors.companyId) : undefined}
                                                                    />
                                                                )}
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="phoneNumber"
                                                                    name="phoneNumber"
                                                                    label="Phone Number"
                                                                    value={values.phoneNumber ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                                                    helperText={touched.phoneNumber && errors.phoneNumber}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="email"
                                                                    name="email"
                                                                    label="Email"
                                                                    value={values.email ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.email && Boolean(errors.email)}
                                                                    helperText={touched.email && errors.email}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Form>

                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Formik
                            initialValues={{
                                role: userRoles ? userRoles : [],
                            }}
                            onSubmit={confirmDialogForRole}
                            validationSchema={
                                userRoles?.length > 0
                                    ? undefined
                                    : validationSchemaForRole
                            }
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                setFieldValue,
                                handleSubmit,
                                resetForm,
                            }) => (
                                <>
                                    <Dialog
                                        open={isUserRoleAttachDialogOpen}
                                        onClose={() => {
                                            closeUserRoleAttachModel(resetForm);
                                        }}
                                        onConfirm={handleSubmit}
                                        title={`${userRoles?.length > 0 ? `Update Role For ${userToAttach?.row?.user_name}` : `Add Role For ${userToAttach?.row?.user_name}`}`}
                                        confirmButtonText={userRoles?.length > 0 ? "Update" : "Add"}
                                        cancelButtonText="Cancel"
                                        icon={<BadgeOutlinedIcon />}
                                        message={
                                            <Box padding={6}>
                                                {loading.update || loading.create ? (
                                                    <Loader
                                                        color="primary"
                                                        size={40}
                                                    />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={12}>
                                                                <Dropdown
                                                                    options={roles?.map(
                                                                        (item) => `${item?.role_name}`
                                                                    )}
                                                                    value={values.role}
                                                                    label="Role"
                                                                    checkmark={true}
                                                                    onChange={(option) =>
                                                                        setFieldValue("role", option)
                                                                    }
                                                                    required
                                                                    error={touched.role && !!errors.role}
                                                                    helperText={
                                                                        touched.role
                                                                            ? Array.isArray(errors.role)
                                                                                ? errors.role.join(", ")
                                                                                : errors.role
                                                                            : ""
                                                                    }
                                                                    showSelectedOptions={true}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Form>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Company" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader
                                            color="primary"
                                            size={40}
                                        />
                                    ) : (
                                        "Are you sure you want to delete this user?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );
};

export default User;
